import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  ScrollView,
  ActivityIndicator,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { DualPaneLayout, Modal, Input, Button, colors, spacing, typography } from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService, MembershipCard, PendingInvitation } from '../services/api';
import { handleApiError, logError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const BusinessPickerScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { showNotification, navigateTo } = useAppContext();
  const { phase1Token, setAuthState, setPhase1Token, user, currentBusiness, setCurrentBusiness } = useAppStore();
  const { t } = useLocale();
  const typo = useTypography();

  const [memberships, setMemberships] = useState<MembershipCard[]>([]);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [respondingCode, setRespondingCode] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MembershipCard | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadMemberships();
  }, []);

  const loadMemberships = async () => {
    setIsLoading(true);
    try {
      if (phase1Token) {
        apiService.setAuthToken(phase1Token);
      }
      const [membershipsResponse, invitationsResponse] = await Promise.all([
        apiService.getMemberships(),
        apiService.getPendingInvitations(),
      ]);
      if (membershipsResponse.statusCode === 1000) {
        setMemberships(membershipsResponse.data || []);
      } else {
        showNotification('error', 'Could not load businesses', membershipsResponse.errors?.[0]?.message || 'Please try again.');
      }
      if (invitationsResponse.statusCode === 1000) {
        setInvitations(invitationsResponse.data || []);
      }
    } catch (err) {
      logError(err, 'BusinessPickerScreen');
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  const onAcceptInvitation = async (invitationCode: string) => {
    setRespondingCode(invitationCode);
    try {
      const response = await apiService.acceptInvitationByCode(invitationCode);
      if (response.statusCode === 1000) {
        setInvitations(prev => prev.filter(inv => inv.invitationCode !== invitationCode));
        await loadMemberships();
      } else {
        showNotification('error', 'Could not accept invitation', response.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setRespondingCode(null);
    }
  };

  const onDeclineInvitation = async (invitationCode: string) => {
    setRespondingCode(invitationCode);
    try {
      const response = await apiService.declineInvitation(invitationCode);
      if (response.statusCode === 1000) {
        setInvitations(prev => prev.filter(inv => inv.invitationCode !== invitationCode));
      } else {
        showNotification('error', 'Could not decline invitation', response.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setRespondingCode(null);
    }
  };

  // Each business is billed on its own: one can be paused while the others
  // work normally, whatever the user's role in each.
  const billingBadge = (m: MembershipCard) => {
    const until = m.billingUntil ? new Date(m.billingUntil).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '';
    const map: Record<string, { label: string; color: string } | undefined> = {
      trial: { label: until ? t('picker.billing.trialUntil', { date: until }) : t('billing.status.trial'), color: colors.status.warning as string },
      grace: { label: t('picker.billing.renewBy', { date: until }), color: colors.status.warning as string },
      locked: { label: t('picker.billing.paused'), color: colors.status.error as string },
      none: { label: t('picker.billing.noPlan'), color: colors.textLight as string },
    };
    const badge = m.billingState ? map[m.billingState] : undefined;
    if (!badge) return null;
    return (
      <View style={[styles.billingBadge, { borderColor: badge.color }]}>
        <Text style={[styles.billingBadgeText, { color: badge.color }]} numberOfLines={1}>{badge.label}</Text>
      </View>
    );
  };

  const onSelectBusiness = async (businessId: string) => {
    setSelectingId(businessId);
    try {
      const response = await apiService.selectBusiness(businessId);
      if (response.statusCode === 1000 && response.data) {
        const { token, refreshToken } = response.data;
        // Set the api-service token BEFORE navigating — HomeScreen's mount effect fires
        // synchronously off the upcoming navigateTo, so the token must already be in place
        // (both on the singleton and in the store) before that render happens, not after.
        apiService.setAuthToken(token);
        apiService.setRefreshToken(refreshToken);
        await setAuthState({
          tokens: { token, refreshToken },
          user: user || {
            id: '', email: '', firstName: '', lastName: '', username: '',
            isEmailVerified: true, isActive: true,
            createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
          },
          source: 'login'
        });
        setPhase1Token(null);
        navigateTo('home');
      } else {
        showNotification('error', 'Could not continue', response.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setSelectingId(null);
    }
  };

  const openDelete = (membership: MembershipCard) => {
    setDeleteConfirmName('');
    setDeleteTarget(membership);
  };

  const deleteNameMatches = !!deleteTarget
    && deleteConfirmName.trim().toLowerCase() === deleteTarget.businessName.trim().toLowerCase();

  // Owner-only, while the server allows it (ALLOW_BUSINESS_DELETE) — a
  // testing aid for onboarding; the API checks ownership itself.
  const onConfirmDelete = async () => {
    if (!deleteTarget || !deleteNameMatches) return;
    setIsDeleting(true);
    try {
      const response = await apiService.deleteOwnedBusiness(deleteTarget.businessId);
      if (response.statusCode === 1000) {
        const deletedId = deleteTarget.businessId;
        setMemberships(prev => prev.filter(m => m.businessId !== deletedId));
        // Deleted the business this session is in: there's nothing to go
        // back to, so stay here like a fresh login until another is chosen.
        if (currentBusiness?._id === deletedId) {
          setCurrentBusiness(null);
          if (!phase1Token) setPhase1Token(apiService.getAuthToken());
        }
        setDeleteTarget(null);
        showNotification('success', t('picker.delete.done'), deleteTarget.businessName);
      } else {
        showNotification('error', t('picker.delete.failed'), response.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err) {
      const { title, message, type } = handleApiError(err);
      showNotification(type, title, message);
    } finally {
      setIsDeleting(false);
    }
  };

  const hasOwnedBusiness = memberships.some(m => m.role === 'owner');
  // Reached mid-session (e.g. "switch business" from the More menu) rather than during
  // onboarding when there's no phase1Token — has an existing session/home to cancel back to.
  const canCancel = !phase1Token;

  return (
    <DualPaneLayout title="DISLIX" subtitle={t('auth.chooseBusiness') || 'Choose a business'}>
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {canCancel && (
            <TouchableOpacity
              onPress={() => navigateTo('home')}
              style={styles.backButton}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <DirectionalIcon name="chevron-back" size={22} color={colors.textDark} />
              <Text style={styles.backButtonText}>{t('common.back')}</Text>
            </TouchableOpacity>
          )}
          <Text style={[styles.title, { fontFamily: typo.fontFamily.bold }]}>
            {t('auth.chooseBusiness') || 'Choose a business'}
          </Text>

          {isLoading ? (
            <ActivityIndicator size="large" color={colors.primary} style={styles.loader} />
          ) : (
            <View style={styles.cards}>
              {invitations.length > 0 && (
                <View style={styles.invitationsSection}>
                  <Text style={[styles.sectionLabel, { fontFamily: typo.fontFamily.medium }]}>
                    {t('auth.pendingInvitations') || 'Invitations'}
                  </Text>
                  {invitations.map((invitation) => {
                    const isResponding = respondingCode === invitation.invitationCode;
                    return (
                      <View key={invitation.invitationCode} style={[styles.card, styles.invitationCard]}>
                        {invitation.businessLogo ? (
                          <Image source={{ uri: invitation.businessLogo }} style={styles.cardLogo} />
                        ) : (
                          <View style={styles.cardLogoPlaceholder}>
                            <Ionicons name="business" size={28} color={colors.primary} />
                          </View>
                        )}
                        <View style={styles.cardInfo}>
                          <Text style={[styles.cardName, { fontFamily: typo.fontFamily.medium }]} numberOfLines={1}>
                            {invitation.businessName}
                          </Text>
                          <Text style={styles.cardRole}>
                            {invitation.invitedByName
                              ? `${invitation.role} · ${t('auth.invitedBy') || 'invited by'} ${invitation.invitedByName}`
                              : invitation.role}
                          </Text>
                          {invitation.isExpired && (
                            <Text style={styles.expiredText}>{t('auth.invitationExpired') || 'Invitation expired'}</Text>
                          )}
                        </View>
                        {isResponding ? (
                          <ActivityIndicator size="small" color={colors.primary} />
                        ) : (
                          <View style={styles.invitationActions}>
                            <TouchableOpacity
                              style={styles.declineButton}
                              onPress={() => onDeclineInvitation(invitation.invitationCode)}
                              disabled={respondingCode !== null}
                              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                            >
                              <Ionicons name="close" size={18} color={colors.textLight} />
                            </TouchableOpacity>
                            {!invitation.isExpired && (
                              <TouchableOpacity
                                style={styles.acceptButton}
                                onPress={() => onAcceptInvitation(invitation.invitationCode)}
                                disabled={respondingCode !== null}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                              >
                                <Ionicons name="checkmark" size={18} color={colors.white} />
                              </TouchableOpacity>
                            )}
                          </View>
                        )}
                      </View>
                    );
                  })}
                </View>
              )}

              {memberships.map((membership) => (
                <TouchableOpacity
                  key={membership.businessId}
                  style={styles.card}
                  onPress={() => onSelectBusiness(membership.businessId)}
                  disabled={selectingId !== null}
                  activeOpacity={0.85}
                >
                  {membership.logo ? (
                    <Image source={{ uri: membership.logo }} style={styles.cardLogo} />
                  ) : (
                    <View style={styles.cardLogoPlaceholder}>
                      <Ionicons name="business" size={28} color={colors.primary} />
                    </View>
                  )}
                  <View style={styles.cardInfo}>
                    <Text style={[styles.cardName, { fontFamily: typo.fontFamily.medium }]} numberOfLines={1}>
                      {membership.businessName}
                    </Text>
                    <View style={styles.cardMetaRow}>
                      <Text style={styles.cardRole}>{membership.role}</Text>
                      {billingBadge(membership)}
                    </View>
                  </View>
                  {membership.canDelete && membership.role === 'owner' && selectingId !== membership.businessId && (
                    <TouchableOpacity
                      style={styles.deleteButton}
                      onPress={() => openDelete(membership)}
                      disabled={selectingId !== null}
                      accessibilityRole="button"
                      accessibilityLabel={t('picker.delete.action')}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Ionicons name="trash-outline" size={20} color={colors.status.error as string} />
                    </TouchableOpacity>
                  )}
                  {selectingId === membership.businessId ? (
                    <ActivityIndicator size="small" color={colors.primary} />
                  ) : (
                    <DirectionalIcon name="chevron-forward" size={20} color={colors.textLight} />
                  )}
                </TouchableOpacity>
              ))}

              <TouchableOpacity
                style={[styles.card, styles.addCard]}
                onPress={() => navigateTo('business-creation-wizard')}
                disabled={selectingId !== null}
                activeOpacity={0.85}
              >
                <View style={styles.cardLogoPlaceholder}>
                  <Ionicons name="add" size={28} color={colors.primary} />
                </View>
                <View style={styles.cardInfo}>
                  <Text style={[styles.cardName, { fontFamily: typo.fontFamily.medium }]}>
                    {hasOwnedBusiness
                      ? (t('auth.addNewBusiness') || 'Add new business')
                      : (t('auth.createYourOwnBusiness') || 'Create your own business account')}
                  </Text>
                </View>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </SafeAreaView>

      <Modal
        visible={!!deleteTarget}
        onClose={() => { if (!isDeleting) setDeleteTarget(null); }}
        title={t('picker.delete.title')}
        size="small"
        closeOnBackdrop={!isDeleting}
      >
        <View style={styles.deleteContent}>
          <Text style={styles.deleteMessage}>
            {t('picker.delete.message', { name: deleteTarget?.businessName || '' })}
          </Text>
          <Input
            value={deleteConfirmName}
            onChangeText={setDeleteConfirmName}
            placeholder={deleteTarget?.businessName || t('picker.delete.placeholder')}
            autoCapitalize="none"
            autoCorrect={false}
            editable={!isDeleting}
            inputStyle={styles.deleteInput}
          />
          <View style={styles.deleteActions}>
            <Button
              title={t('common.cancel')}
              variant="outline"
              onPress={() => setDeleteTarget(null)}
              disabled={isDeleting}
              style={styles.deleteActionButton}
            />
            <Button
              title={t('common.delete')}
              onPress={onConfirmDelete}
              loading={isDeleting}
              disabled={!deleteNameMatches}
              style={StyleSheet.flatten([styles.deleteActionButton, styles.deleteConfirmButton, !deleteNameMatches && styles.deleteConfirmDisabled])}
            />
          </View>
        </View>
      </Modal>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: spacing.md,
  },
  backButtonText: {
    fontSize: 15,
    color: colors.textDark,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    color: colors.textDark,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  deleteButton: {
    padding: spacing.xs,
    marginHorizontal: spacing.xs,
  },
  deleteContent: {
    gap: spacing.md,
  },
  deleteMessage: {
    fontSize: typography.fontSize.base,
    lineHeight: typography.fontSize.base * 1.5,
    color: colors.textDark,
  },
  deleteInput: {
    minWidth: 0,
  },
  deleteActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  deleteActionButton: {
    flex: 1,
  },
  deleteConfirmButton: {
    backgroundColor: colors.status.error as string,
    borderColor: colors.status.error as string,
  },
  deleteConfirmDisabled: {
    opacity: 0.5,
  },
  loader: {
    marginTop: spacing.xl * 2,
  },
  cards: {
    gap: spacing.md,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: spacing.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    gap: spacing.md,
  },
  addCard: {
    borderStyle: 'dashed',
    borderColor: colors.primary,
  },
  cardLogo: {
    width: 44,
    height: 44,
    borderRadius: spacing.borderRadius.md,
  },
  cardLogoPlaceholder: {
    width: 44,
    height: 44,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.primaryTransparent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardInfo: {
    flex: 1,
  },
  cardName: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
  },
  cardRole: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    textTransform: 'capitalize',
  },
  cardMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  billingBadge: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  billingBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  invitationsSection: {
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  sectionLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  invitationCard: {
    borderColor: colors.primary,
    borderStyle: 'dashed',
  },
  expiredText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    marginTop: 2,
  },
  invitationActions: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  declineButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  acceptButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
